import { useEffect, useState } from "react"
import { useAtom } from "jotai"
import { useQueryClient } from "@tanstack/react-query"
import { ImSpinner2 } from "react-icons/im"
import { Drawer, DrawerContent, DrawerTitle } from "@mylaunch/components/ui"
import { useAppMutation } from "~/hooks/useAppMutation"
import { useAppQuery } from "~/hooks/useAppQuery"
import { Button } from "~/components/ui/button"
import { TextField } from "~/components/ui/text-field"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select"
import type { PlanApplicationSphere } from "@apps/gwservice/types"
import { planApplicationAtom } from "../atoms/plan-application"
import { plansQueryKey, usePlans } from "../hooks/use-plans"
import { applyForPlan, getPlanApplicationPrefill, type PlanApplicationFormInput } from "../server/plans.functions"

const SPHERE_OPTIONS: { value: PlanApplicationSphere; label: string }[] = [
  { value: "entertainment", label: "Развлечения" },
  { value: "non-profit", label: "Не для получения прибыли" },
  { value: "other", label: "Другое" },
]

const emptyForm = (): PlanApplicationFormInput => ({
  lastName: "",
  firstName: "",
  middleName: "",
  birthDate: "",
  sphere: "entertainment",
  position: "",
  phone: "",
  email: "",
  source: "",
  recommender: "",
  expectations: "",
})

const prefillQueryKey = ["plan-application", "prefill"] as const

function usePlanApplicationPrefill() {
  return useAppQuery({
    queryKey: prefillQueryKey,
    queryFn: () => getPlanApplicationPrefill(),
    errorMessage: "Не удалось загрузить данные пользователя",
  })
}

function DateField({
  label,
  value,
  onChange,
  required,
  disabled,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  required?: boolean
  disabled?: boolean
}) {
  return (
    <div className="flex flex-col gap-xs">
      <label className="text-sm text-secondary font-medium">
        {label}
        {required && <span className="text-destructive ml-xs">*</span>}
      </label>
      <input
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        disabled={disabled}
        className="text-base min-h-edit-field border rounded-md p-s"
      />
    </div>
  )
}

function TextareaField({
  label,
  value,
  onChange,
  disabled,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  disabled?: boolean
}) {
  return (
    <div className="flex flex-col gap-xs">
      <label className="text-sm text-secondary font-medium">{label}</label>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        rows={3}
        className="text-base border rounded-md p-s"
      />
    </div>
  )
}

function SphereField({
  value,
  onChange,
  disabled,
}: {
  value: PlanApplicationSphere
  onChange: (v: PlanApplicationSphere) => void
  disabled?: boolean
}) {
  return (
    <div className="flex flex-col gap-xs">
      <label className="text-sm text-secondary font-medium">
        Сфера деятельности
        <span className="text-destructive ml-xs">*</span>
      </label>
      <Select value={value} onValueChange={(v) => onChange(v as PlanApplicationSphere)} disabled={disabled}>
        <SelectTrigger className="w-full min-h-edit-field">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {SPHERE_OPTIONS.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

function ApplicationForm({ planId, planName }: { planId: number; planName: string }) {
  const [, setOpen] = useAtom(planApplicationAtom)
  const queryClient = useQueryClient()
  const { data: prefill } = usePlanApplicationPrefill()
  const [form, setForm] = useState<PlanApplicationFormInput>(emptyForm())
  const [consent, setConsent] = useState(false)
  const [touched, setTouched] = useState(false)

  useEffect(() => {
    if (!prefill) return
    setForm((prev) => ({
      ...prev,
      email: prev.email || prefill.email,
      phone: prev.phone || prefill.phone,
      position: prev.position || prefill.position,
    }))
  }, [prefill])

  const update = <K extends keyof PlanApplicationFormInput>(key: K, value: PlanApplicationFormInput[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  const mutation = useAppMutation({
    mutationFn: () => applyForPlan({ data: { planId, form, consent } }),
    errorMessage: "Не удалось отправить заявку",
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: plansQueryKey })
      setOpen(null)
    },
  })

  const missingRequired =
    !form.lastName.trim() ||
    !form.firstName.trim() ||
    !form.middleName.trim() ||
    !form.birthDate.trim() ||
    !form.position.trim() ||
    !form.phone.trim() ||
    !form.email.trim()
  const canSubmit = consent && !missingRequired && !mutation.isPending

  const handleSubmit = () => {
    setTouched(true)
    if (!canSubmit) return
    mutation.mutate()
  }

  const disabled = mutation.isPending

  return (
    <div className="flex flex-col gap-m p-m">
      <h2 className="text-xl font-bold">Заявка на тариф «{planName}»</h2>
      <TextField label="Фамилия" value={form.lastName} onChange={(v) => update("lastName", v)} required disabled={disabled} />
      <TextField label="Имя" value={form.firstName} onChange={(v) => update("firstName", v)} required disabled={disabled} />
      <TextField label="Отчество" value={form.middleName} onChange={(v) => update("middleName", v)} required disabled={disabled} />
      <DateField label="Дата рождения" value={form.birthDate} onChange={(v) => update("birthDate", v)} required disabled={disabled} />
      <SphereField value={form.sphere} onChange={(v) => update("sphere", v)} disabled={disabled} />
      <TextField label="Должность" value={form.position} onChange={(v) => update("position", v)} required disabled={disabled} />
      <TextField label="Телефон" value={form.phone} onChange={(v) => update("phone", v)} required disabled={disabled} />
      <TextField label="E-mail" value={form.email} onChange={(v) => update("email", v)} required disabled={disabled} />
      <TextField label="Откуда узнали о платформе" value={form.source} onChange={(v) => update("source", v)} disabled={disabled} />
      <TextField label="Кто может рекомендовать вас" value={form.recommender} onChange={(v) => update("recommender", v)} disabled={disabled} />
      <TextareaField label="Что полезного хотели бы получить от платформы" value={form.expectations} onChange={(v) => update("expectations", v)} disabled={disabled} />
      <label className="flex items-start gap-s text-sm">
        <input
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          disabled={disabled}
          className="mt-xs"
        />
        <span>
          Я согласен(на) на обработку персональных данных в соответствии с 152-ФЗ.
        </span>
      </label>
      {touched && !consent && <p className="text-destructive text-sm">Подтвердите согласие на обработку персональных данных.</p>}
      {touched && missingRequired && <p className="text-destructive text-sm">Заполните все обязательные поля.</p>}
      <Button size="lg" disabled={disabled} onClick={handleSubmit}>
        {mutation.isPending ? <ImSpinner2 className="animate-spin" /> : "Отправить заявку"}
      </Button>
    </div>
  )
}

export function PlanApplicationSheet() {
  const [state, setState] = useAtom(planApplicationAtom)
  const { data } = usePlans()
  const open = state !== null
  const plan = state ? data.plans.find((p) => p.id === state.planId) : undefined

  return (
    <Drawer open={open} onOpenChange={(v) => !v && setState(null)}>
      <DrawerContent
        handle={false}
        showCloseButton
        className="rounded-t-xl p-0 gap-0 border-0 outline-none max-h-[90vh]"
      >
        <DrawerTitle className="sr-only">Заявка на тариф</DrawerTitle>
        <div className="flex-1 min-h-0 overflow-y-auto">
          {state && plan && <ApplicationForm planId={state.planId} planName={plan.name} />}
        </div>
      </DrawerContent>
    </Drawer>
  )
}
